import { OrganizationId, SurveyId, UserId, UserInfo } from "../types";

export type FeatureFlagPayload = {
  /** global override to enable a feature flag */
  enabled: boolean;
  studies?: SurveyId[];
  users?: UserId[];
  orgs?: string[];
  keys?: string[];
  /** studies to exclude even when `enabled` is true */
  excludedStudies?: SurveyId[];
  /** orgs to exclude even when `enabled` is true */
  excludedOrgs?: string[];
};

export type CachedFeatureFlagPayload = FeatureFlagPayload & {
  no_FF_or_no_match?: boolean;
};

const isUserOrStudyOrWorkspaceInPayload = ({
  payload,
  survey_id,
  user,
  userId,
  orgId: workspaceId,
  onlyActiveOrg: onlyActiveWorkspace = false,
}: {
  payload: FeatureFlagPayload;
  survey_id?: SurveyId | null;
  user?: UserInfo | null;
  userId?: UserId | null;
  orgId?: OrganizationId | null;
  onlyActiveOrg?: boolean;
}) => {
  let res = false;
  if (survey_id) {
    res ||= payload["studies"]?.includes(survey_id) ?? false;
  }
  const resolvedUserId = user?.id ?? userId;
  if (resolvedUserId) {
    res ||= payload["users"]?.includes(resolvedUserId) ?? false;
  }

  if (user?.orgs && !onlyActiveWorkspace) {
    res ||= user?.orgs.some(
      (workspace) => payload["orgs"]?.includes(workspace.id) ?? false
    );
  }

  if (workspaceId) {
    res ||= payload["orgs"]?.includes(workspaceId) ?? false;
  }
  return res;
};

const isExcludedFromPayload = ({
  payload,
  survey_id,
  orgId: workspaceId,
}: {
  payload: FeatureFlagPayload;
  survey_id?: SurveyId | null;
  orgId?: OrganizationId | null;
}) => {
  if (survey_id && payload["excludedStudies"]?.includes(survey_id)) {
    return true;
  }
  if (workspaceId && payload["excludedOrgs"]?.includes(workspaceId)) {
    return true;
  }
  return false;
};

/**
 * Resolves a feature flag payload to an enabled/disabled boolean:
 *   excluded study/org → false
 *   `enabled: true`    → true
 *   otherwise          → study/user/org in the allow-list
 */
export const isFeatureEnabledFromPayload = ({
  payload,
  survey_id,
  user,
  userId,
  orgId,
  onlyActiveOrg = false,
}: {
  payload: FeatureFlagPayload;
  survey_id?: SurveyId | null;
  user?: UserInfo | null;
  /** For callers with only ids, such as the typed flag SDK. */
  userId?: UserId | null;
  orgId?: OrganizationId | null;
  onlyActiveOrg?: boolean;
}): boolean => {
  if (isExcludedFromPayload({ payload, survey_id, orgId })) {
    return false;
  }
  if (payload["enabled"]) {
    return true;
  }
  return isUserOrStudyOrWorkspaceInPayload({
    payload,
    survey_id,
    user,
    userId,
    orgId,
    onlyActiveOrg,
  });
};
